import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, ChevronRight } from "lucide-react";

export const ReviewsDashboardCard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reviews/summary`).then(r => setSummary(r.data)).catch(() => {});
  }, []);

  if (!summary) return null;

  return (
    <Card data-testid="reviews-dashboard-card" className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/reviews")}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500 fill-amber-500" /> Google Reviews
          </CardTitle>
          <Button variant="ghost" size="sm" className="text-indigo-600" data-testid="reviews-card-open">
            Open <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          <Stat value={summary.sent_this_month} label="Requests this month" testId="reviews-card-month" />
          <Stat value={summary.sent_total} label="All time" testId="reviews-card-total" />
          <Stat value={summary.not_asked_count} label="Not yet asked" highlight={summary.not_asked_count > 0} testId="reviews-card-not-asked" />
        </div>
        {summary.not_asked?.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <p className="text-xs text-slate-500 mb-2">Completed projects waiting for a review request:</p>
            <div className="flex flex-wrap gap-2">
              {summary.not_asked.slice(0, 5).map(c => (
                <span key={c.lead_id} className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2.5 py-1">
                  {c.first_name} {c.last_name}
                </span>
              ))}
              {summary.not_asked.length > 5 && <span className="text-xs text-slate-400 py-1">+{summary.not_asked.length - 5} more</span>}
            </div>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-3">
          Auto-send {summary.auto_enabled ? `on · ${summary.auto_delay_days === 0 ? "same day as completion" : `${summary.auto_delay_days} day(s) after completion`}` : "off"}
          {" · "}Reminders {summary.reminder_enabled ? `every ${summary.reminder_interval_days} days` : "off"}
          {summary.reviewed_total > 0 && ` · ${summary.reviewed_total} marked reviewed`}
        </p>
      </CardContent>
    </Card>
  );
};

const Stat = ({ value, label, highlight, testId }) => (
  <div data-testid={testId}>
    <p className={`text-2xl font-bold ${highlight ? "text-amber-600" : "text-slate-900"}`}>{value ?? 0}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);
